import { useState } from 'react';
import { useQuery, useMutation, useLazyQuery } from '@apollo/client';
import {
  ArrowLeft,
  Calendar,
  Users,
  Link as LinkIcon,
  Plus,
  MoreVertical,
  LayoutGrid,
  List,
  Columns,
  Clock,
  CheckCircle2,
  Circle,
  AlertCircle,
  CalendarDays,
  Eye,
  Sparkles,
  Loader2,
  Edit,
  Save,
  X,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Progress } from '../ui/progress';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from '../ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { TimelineCalendar } from './TimelineCalendar';
import { KanbanBoard } from './KanbanBoard';
import { AddTeamMemberModal } from './AddTeamMemberModal';
import { CreateProjectModal } from './CreateProjectModal';
import { CreatePostModal } from './CreatePostModal';
import { PlatformIcon } from '../common/PlatformIcon';
import {
  GET_PROJECT,
  GET_PROJECT_POSTS,
  GENERATE_SOLUTION,
  GET_SOCIAL_PROFILES,
} from '../../graphql/queries';
import {
  UPDATE_PROJECT_POST,
  CREATE_PROJECT_POST,
  ADD_PROJECT_POST_TARGET,
  DELETE_PROJECT_POST,
  UPDATE_PROJECT,
} from '../../graphql/mutations';
import { toast } from 'sonner';
import { endedAs, phaseOf } from '../reach/campaignPhase';
import { briefExcerpt, calendarDate, shortDate } from '../reach/reach';
import { GetSocialProfilesResponse } from '../../types/graphql';
import { useAccount } from '../../context/AccountContext';
import { Loader } from '../common/PageLoader';

export interface SocialPost {
  id: string;
  title: string;
  content: string;
  platforms: string[];
  status: 'draft' | 'in-review' | 'approved' | 'scheduled' | 'published';
  assignedTo?: string;
  scheduledDate?: string;
  dueDate: string;
  postType?: string;
  notes?: string;
  topic?: {
    id: string;
    title: string;
    summary: string;
    mediaType: string;
    mediaUrl: string;
    destinationUrl: string;
    keywords?: string;
  };
  targets?: Array<{
    id: string;
    platform: string;
    socialProfileId: string;
    socialProfile?: {
      id: string;
      profileName: string;
      platform: string;
    };
  }>;
}

interface ProjectDetailProps {
  projectId: string;
  onBack: () => void;
  onCreatePost: () => void;
}

export function ProjectDetail({ projectId, onBack, onCreatePost }: ProjectDetailProps) {
  const [viewMode, setViewMode] = useState<'grid' | 'list' | 'kanban'>('grid');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [timelineView, setTimelineView] = useState<'list' | 'calendar'>('list');
  const [isAddMemberModalOpen, setIsAddMemberModalOpen] = useState(false);
  const [isEditProjectModalOpen, setIsEditProjectModalOpen] = useState(false);
  const [isEditPostModalOpen, setIsEditPostModalOpen] = useState(false);
  const [postToEdit, setPostToEdit] = useState<any>(null);
  const [isAIPlanModalOpen, setIsAIPlanModalOpen] = useState(false);
  const [aiPlan, setAiPlan] = useState<{ solution: string; plan: string[] } | null>(null);
  const [isGeneratingPlan, setIsGeneratingPlan] = useState(false);
  const [isCreatePostFromPlanOpen, setIsCreatePostFromPlanOpen] = useState(false);
  const [postFromPlanData, setPostFromPlanData] = useState<{
    platforms: string[];
    content: string;
    title: string;
    selectedProfiles?: Record<string, string>;
  } | null>(null);
  const [isEditingPlan, setIsEditingPlan] = useState(false);
  const [editedPlan, setEditedPlan] = useState('');

  // Fetch project data
  const {
    data: projectData,
    loading: projectLoading,
    error: projectError,
  } = useQuery(GET_PROJECT, {
    variables: { id: projectId },
  });

  // Fetch project posts
  const {
    data: postsData,
    loading: postsLoading,
    error: postsError,
  } = useQuery(GET_PROJECT_POSTS, {
    variables: { projectId },
  });

  // Mutation to update post status
  const [updateProjectPost] = useMutation(UPDATE_PROJECT_POST, {
    refetchQueries: [{ query: GET_PROJECT_POSTS, variables: { projectId } }],
  });

  // Mutation to create post (for duplicate)
  const [createProjectPost] = useMutation(CREATE_PROJECT_POST, {
    refetchQueries: [{ query: GET_PROJECT_POSTS, variables: { projectId } }],
  });

  // Mutation to add post target
  const [addProjectPostTarget] = useMutation(ADD_PROJECT_POST_TARGET);

  // Mutation to delete post
  const [deleteProjectPost] = useMutation(DELETE_PROJECT_POST, {
    refetchQueries: [{ query: GET_PROJECT_POSTS, variables: { projectId } }],
  });

  // Mutation to update project
  const [updateProject, { loading: isUpdatingProject }] = useMutation(UPDATE_PROJECT, {
    refetchQueries: [{ query: GET_PROJECT, variables: { id: projectId } }],
  });

  // Query to generate AI solution
  const [generateSolution, { loading: isGeneratingSolution }] = useLazyQuery(GENERATE_SOLUTION);

  const { accountId } = useAccount();

  // Fetch social profiles for pre-selection
  const { data: profilesData } = useQuery<GetSocialProfilesResponse>(GET_SOCIAL_PROFILES, {
    variables: { accountId },
    skip: !accountId,
  });

  // Handler for post status changes from KanbanBoard
  const handlePostStatusChange = async (postId: string, newStatus: SocialPost['status']) => {
    try {
      // Convert status from frontend UI format (in-review) to GraphQL enum format (in_review)
      // Frontend UI uses: 'draft', 'in-review', 'approved', 'scheduled', 'published' (with hyphens)
      // GraphQL enum uses: 'draft', 'in_review', 'approved', 'scheduled', 'published' (with underscores)
      // Database now uses: 'draft', 'in_review', 'approved', 'scheduled', 'published' (matches GraphQL enum)
      const statusMap: Record<SocialPost['status'], string> = {
        draft: 'draft',
        'in-review': 'in_review',
        approved: 'approved',
        scheduled: 'scheduled',
        published: 'published',
      };

      const graphqlStatus = statusMap[newStatus];

      if (!graphqlStatus) {
        console.error('Invalid status:', newStatus);
        return;
      }

      await updateProjectPost({
        variables: {
          id: postId,
          input: {
            status: graphqlStatus,
          },
        },
      });
    } catch (error: any) {
      console.error('Error updating post status:', error);
    }
  };

  if (projectLoading || postsLoading) {
    return (
      <div className="min-h-screen bg-cmp-surface flex items-center justify-center">
        <Loader label="Loading project" className="text-gray-500" />
      </div>
    );
  }

  if (projectError || postsError) {
    return (
      <div className="min-h-screen bg-cmp-surface flex items-center justify-center">
        <p className="text-cmp-crit">
          Error loading project: {projectError?.message || postsError?.message}
        </p>
      </div>
    );
  }

  const project = projectData?.project;
  if (!project) {
    return (
      <div className="min-h-screen bg-cmp-surface flex items-center justify-center">
        <p className="text-gray-500">Campaign not found</p>
      </div>
    );
  }

  // Store raw posts data for editing
  const rawPosts = postsData?.projectPosts || [];

  // Transform posts data
  const posts: SocialPost[] =
    rawPosts.map((p: any) => ({
      id: p.id,
      title: p.title,
      content: p.content,
      platforms: p.targets?.map((t: any) => t.platform) || [],
      status: p.status.toLowerCase().replace(/_/g, '-') as SocialPost['status'],
      assignedTo: p.assignedUser?.email,
      scheduledDate: p.scheduledDate,
      dueDate: p.dueDate,
      postType: p.postType,
      topic: p.topic,
      notes: p.notes,
      targets: p.targets || [],
    })) || [];

  // Handler to open edit modal
  const handleEditPost = (postId: string) => {
    const rawPost = rawPosts.find((p: any) => p.id === postId);
    if (rawPost) {
      setPostToEdit({
        id: rawPost.id,
        title: rawPost.title,
        content: rawPost.content,
        status: rawPost.status,
        assignedTo: rawPost.assignedTo,
        scheduledDate: rawPost.scheduledDate,
        dueDate: rawPost.dueDate,
        postType: rawPost.postType,
        topicId: rawPost.topicId,
        topic: rawPost.topic,
        notes: rawPost.notes,
        targets:
          rawPost.targets?.map((t: any) => ({
            id: t.id,
            platform: t.platform,
            socialProfileId: t.socialProfileId,
          })) || [],
      });
      setIsEditPostModalOpen(true);
    }
  };

  // Handler to change post status
  const handleChangeStatus = async (postId: string, newStatus: string) => {
    try {
      const statusMap: Record<string, string> = {
        draft: 'draft',
        'in-review': 'in_review',
        approved: 'approved',
        scheduled: 'scheduled',
        published: 'published',
      };

      const graphqlStatus = statusMap[newStatus];
      if (!graphqlStatus) {
        toast.error('Invalid status selected');
        return;
      }

      await updateProjectPost({
        variables: {
          id: postId,
          input: {
            status: graphqlStatus,
          },
        },
      });
      toast.success('Post status updated successfully');
    } catch (error: any) {
      toast.error(`Failed to update status: ${error.message}`);
    }
  };

  // Handler to duplicate post
  const handleDuplicatePost = async (postId: string) => {
    try {
      const rawPost = rawPosts.find((p: any) => p.id === postId);
      if (!rawPost) {
        toast.error('Post not found');
        return;
      }

      // Create new post with same data
      const { data } = await createProjectPost({
        variables: {
          input: {
            projectId,
            accountId: project.accountId,
            title: `${rawPost.title} (Copy)`,
            content: rawPost.content,
            status: 'draft', // Always duplicate as draft
            assignedTo: rawPost.assignedTo || undefined,
            scheduledDate: rawPost.scheduledDate || undefined,
            dueDate: rawPost.dueDate,
          },
        },
      });

      const newPostId = data.createProjectPost.id;

      // Copy all targets
      if (rawPost.targets && rawPost.targets.length > 0) {
        const targetPromises = rawPost.targets.map((target: any) =>
          addProjectPostTarget({
            variables: {
              input: {
                projectPostId: newPostId,
                socialProfileId: target.socialProfileId,
                platform: target.platform,
              },
            },
          })
        );

        await Promise.all(targetPromises);
      }

      toast.success('Post duplicated successfully');
    } catch (error: any) {
      toast.error(`Failed to duplicate post: ${error.message}`);
    }
  };

  // Handler to delete post
  const handleDeletePost = async (postId: string) => {
    if (
      !window.confirm('Are you sure you want to delete this post? This action cannot be undone.')
    ) {
      return;
    }

    try {
      await deleteProjectPost({
        variables: { id: postId },
      });
      toast.success('Post deleted successfully');
    } catch (error: any) {
      toast.error(`Failed to delete post: ${error.message}`);
    }
  };

  // Utility function to parse plan item and detect platform
  const parsePlanItem = (item: string): { platforms: string[]; content: string; title: string } => {
    const platforms: string[] = [];
    let content = item;
    let title = '';

    // Detect platforms (case-insensitive)
    // Note: Twitter is now X, but we check for both
    const platformKeywords: Record<string, string[]> = {
      Facebook: ['facebook post', 'facebook', 'fb'],
      Instagram: ['instagram post', 'instagram', 'ig'],
      X: ['twitter post', 'twitter', 'tweet', 'x post', ' x '],
      LinkedIn: ['linkedin post', 'linkedin', 'linked in'],
      Threads: ['threads post', 'threads'],
      YouTube: ['youtube post', 'youtube', 'yt'],
      TikTok: ['tiktok post', 'tiktok', 'tik tok'],
      Pinterest: ['pinterest post', 'pinterest'],
    };

    // Check for platform mentions
    const itemLower = item.toLowerCase();

    // Special handling for Twitter/X - map Twitter mentions to X platform
    if (itemLower.includes('twitter') || itemLower.includes('tweet')) {
      if (!platforms.includes('X')) {
        platforms.push('X');
      }
    }

    // Check other platforms
    for (const [platform, keywords] of Object.entries(platformKeywords)) {
      if (platform === 'X') continue; // Already handled above

      for (const keyword of keywords) {
        if (itemLower.includes(keyword)) {
          // Add platform if not already added
          if (!platforms.includes(platform)) {
            platforms.push(platform);
          }
        }
      }
    }

    // Extract suggested content/caption
    // Look for patterns like "Caption could read:" or "Tweet could be:" or quoted text
    const captionPatterns = [
      /(?:caption|caption could read|tweet|tweet could be|post|content)[\s:]+["']([^"']+)["']/i,
      /(?:should read|could read|might read)[\s:]+["']([^"']+)["']/i,
      /:[\s]*["']([^"']+)["']/,
    ];

    for (const pattern of captionPatterns) {
      const match = item.match(pattern);
      if (match && match[1]) {
        content = match[1].trim();
        break;
      }
    }

    // Generate a title from the first part of the item
    const titleMatch = item.match(/^\d+\.\s*\*\*([^*]+)\*\*/);
    if (titleMatch) {
      title = titleMatch[1].trim();
    } else {
      // Try to extract title from format like "1. **Facebook Post (Teaser/Announcement)**:"
      const altTitleMatch = item.match(/^\d+\.\s*\*\*([^*]+)\*\*/);
      if (altTitleMatch) {
        title = altTitleMatch[1].trim();
      } else {
        // Fallback: use first sentence or first 50 chars
        const firstSentence = item
          .split(/[.!?]/)[0]
          .replace(/^\d+\.\s*\*\*|\*\*/g, '')
          .trim();
        title = firstSentence.length > 50 ? firstSentence.substring(0, 50) + '...' : firstSentence;
      }
    }

    return {
      platforms: platforms.length > 0 ? platforms : [],
      content: content || item,
      title: title || 'New Post',
    };
  };

  // Handler to create post from plan item
  const handleCreatePostFromPlanItem = (planItem: string) => {
    const parsed = parsePlanItem(planItem);

    // Find and pre-select social profiles for each detected platform
    const selectedProfiles: Record<string, string> = {};
    if (profilesData?.socialProfiles && parsed.platforms.length > 0) {
      parsed.platforms.forEach((platform) => {
        // Find first available profile for this platform
        const availableProfile = profilesData.socialProfiles.find((p) => p.platform === platform);
        if (availableProfile) {
          selectedProfiles[platform] = availableProfile.id;
        }
      });
    }

    setPostFromPlanData({
      ...parsed,
      selectedProfiles: Object.keys(selectedProfiles).length > 0 ? selectedProfiles : undefined,
    });
    setIsAIPlanModalOpen(false);
    setIsCreatePostFromPlanOpen(true);
  };

  // Handler to start editing plan
  const handleStartEditingPlan = () => {
    setEditedPlan(projectDisplay.plan);
    setIsEditingPlan(true);
  };

  // Handler to cancel editing plan
  const handleCancelEditingPlan = () => {
    setEditedPlan('');
    setIsEditingPlan(false);
  };

  // Handler to save plan
  const handleSavePlan = async () => {
    try {
      await updateProject({
        variables: {
          id: projectId,
          input: {
            plan: editedPlan,
          },
        },
      });
      toast.success('Project plan updated successfully');
      setIsEditingPlan(false);
      setEditedPlan('');
    } catch (error: any) {
      toast.error(`Failed to update project plan: ${error.message}`);
    }
  };

  // Handler to save AI plan to project plan
  const handleSaveAIPlanToProject = async () => {
    if (!aiPlan) {
      toast.error('No AI plan available to save');
      return;
    }

    try {
      // Format the AI plan: solution + plan items
      let formattedPlan = '';

      if (aiPlan.solution) {
        formattedPlan += `STRATEGY OVERVIEW\n${aiPlan.solution}\n\n`;
      }

      if (aiPlan.plan && aiPlan.plan.length > 0) {
        formattedPlan += 'ACTION PLAN\n';
        aiPlan.plan.forEach((item, index) => {
          // Skip guidelines (items starting with "-")
          if (!item.trim().startsWith('-')) {
            formattedPlan += `${index + 1}. ${item}\n`;
          }
        });
      }

      await updateProject({
        variables: {
          id: projectId,
          input: {
            plan: formattedPlan.trim(),
          },
        },
      });

      toast.success('AI plan saved to Plan successfully');
      setIsAIPlanModalOpen(false);
      setAiPlan(null);
    } catch (error: any) {
      toast.error(`Failed to save AI plan to project: ${error.message}`);
    }
  };

  // Handler to generate AI social media plan
  const handleGenerateAIPlan = async () => {
    if (!project) {
      toast.error('Project data not available');
      return;
    }

    const campaignStory = project.campaignStory || '';

    if (!campaignStory) {
      toast.error('Please add a project brief to generate an AI plan');
      return;
    }

    setIsAIPlanModalOpen(true);
    setIsGeneratingPlan(true);

    // Use project brief in situation string
    const situation = `Project: ${project.name}\n\nProject Brief: ${campaignStory}`;

    try {
      const { data } = await generateSolution({
        variables: { situation },
        fetchPolicy: 'network-only', // Force a fresh query, bypass cache
      });

      if (data?.generateSolution) {
        setAiPlan({
          solution: data.generateSolution.solution,
          plan: data.generateSolution.plan || [],
        });
      }
    } catch (error: any) {
      toast.error(`Failed to generate AI plan: ${error.message}`);
      setIsAIPlanModalOpen(false);
    } finally {
      setIsGeneratingPlan(false);
    }
  };

  // Transform project data
  // Where it is, read exactly as Reach → Campaigns reads it (never the raw status).
  const dated = { status: project.status, deadline: project.deadline, posts: project.posts || [] };
  const phase = phaseOf(dated);
  const phaseLabel =
    phase === 'ended' ? endedAs(dated).status : phase === 'running' ? 'Running' : 'Upcoming';
  const projectDisplay = {
    id: project.id,
    name: project.name,
    plan: project.plan || '',
    deadline: project.deadline,
    status: project.status.toLowerCase(),
    progress: project.progress || 0,
    campaignStory: project.campaignStory || '',
    landingPageUrl: project.landingPageUrl || '',
    landingPageStatus: project.landingPageStatus || '',
    notes: project.notes || '',
    teamMembers:
      project.teamMembers?.map((tm: any) => ({
        name: tm.user?.email || 'Unknown',
        role: tm.role || 'Team Member',
        avatar: tm.user?.email?.charAt(0).toUpperCase() || '?',
      })) || [],
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'published':
        return <CheckCircle2 className="w-4 h-4 text-cmp-ok" />;
      case 'scheduled':
        return <Clock className="w-4 h-4 text-cmp-ink-3" />;
      case 'approved':
        return <CheckCircle2 className="w-4 h-4 text-cmp-ink-3" />;
      case 'in-review':
        return <AlertCircle className="w-4 h-4 text-cmp-warn" />;
      case 'draft':
        return <Circle className="w-4 h-4 text-gray-400" />;
      default:
        return <Circle className="w-4 h-4 text-gray-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'published':
        return 'bg-cmp-ok-soft text-cmp-ok border border-cmp-line';
      case 'scheduled':
        return 'bg-cmp-surface text-cmp-ink-2 border border-cmp-line';
      case 'approved':
        return 'bg-cmp-surface text-cmp-ink-2 border border-cmp-line';
      case 'in-review':
        return 'bg-cmp-warn-soft text-cmp-warn border border-cmp-line';
      case 'draft':
        return 'bg-cmp-surface text-cmp-ink-2';
      default:
        return 'bg-cmp-surface text-cmp-ink-2';
    }
  };

  const getPlatformColor = (platform: string) => {
    switch (platform.toLowerCase()) {
      case 'twitter':
      case 'x':
        return 'bg-black text-white'; // X/Twitter is black
      case 'linkedin':
        return 'bg-blue-600 text-onfill'; // LinkedIn blue
      case 'instagram':
        return 'bg-gradient-to-r from-purple-600 via-pink-600 to-orange-500 text-white'; // Instagram gradient
      case 'facebook':
        return 'bg-blue-600 text-onfill'; // Facebook blue
      case 'youtube':
        return 'bg-red-600 text-onfill'; // YouTube red
      case 'threads':
        return 'bg-black text-white'; // Threads is black
      case 'tiktok':
        return 'bg-black text-white'; // TikTok is black
      case 'bluesky':
        return 'bg-sky-500 text-onfill'; // BlueSky sky blue
      case 'snapchat':
        return 'bg-yellow-400 text-[#17140f]'; // Snapchat yellow (brand colour: fixed dark label)
      case 'pinterest':
        return 'bg-red-600 text-onfill'; // Pinterest red
      default:
        return 'bg-gray-600 text-white';
    }
  };

  const filteredPosts = posts.filter((post) => {
    return statusFilter === 'all' || post.status === statusFilter;
  });

  const statusCounts = {
    all: posts.length,
    draft: posts.filter((p) => p.status === 'draft').length,
    'in-review': posts.filter((p) => p.status === 'in-review').length,
    approved: posts.filter((p) => p.status === 'approved').length,
    scheduled: posts.filter((p) => p.status === 'scheduled').length,
    published: posts.filter((p) => p.status === 'published').length,
  };

  return (
    <div className="h-full flex flex-col bg-core-dark text-white overflow-hidden">
      {/* Header */}
      <div className="border-b bg-core-dark text-white flex-shrink-0">
        <div className="px-8 py-6 bg-core-dark text-white">
          <div className="max-w-7xl px-4">
            <Button variant="ghost" onClick={onBack} className="mb-4 gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back to campaigns
            </Button>

            <div className="flex items-start justify-between bg-core-dark text-white">
              <div className="flex-1">
                <h1 className="font-cmp-serif text-cmp-title text-cmp-ink mb-2">
                  {projectDisplay.name}
                </h1>
                {projectDisplay.campaignStory && (
                  <p className="text-cmp-ink-2 mb-4 line-clamp-2">
                    {briefExcerpt(projectDisplay.campaignStory, 240)}
                  </p>
                )}

                <div className="flex gap-4 items-center">
                  <Badge
                    className="bg-cmp-surface text-cmp-ink-2 border border-cmp-line"
                    variant="secondary"
                  >
                    {phaseLabel}
                  </Badge>
                  {calendarDate(projectDisplay.deadline) ? (
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      <span>
                        {phase === 'ended' ? 'Ended' : 'Ends'}{' '}
                        {shortDate(calendarDate(projectDisplay.deadline))}
                      </span>
                    </div>
                  ) : null}
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    <span>{projectDisplay.teamMembers.length} team members</span>
                  </div>
                </div>
              </div>

              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setIsEditProjectModalOpen(true)}>
                  Edit Project
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="icon" className="bg-core-dark">
                      <MoreVertical className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-core-dark">
                    <DropdownMenuItem className="hover:bg-form-dark">Duplicate</DropdownMenuItem>
                    <DropdownMenuItem className="hover:bg-form-dark">Archive</DropdownMenuItem>
                    <DropdownMenuItem className="text-cmp-crit hover:bg-cmp-bronze-soft">
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="px-8 py-8">
          <div className="max-w-7xl px-4">
            <Tabs defaultValue="overview" className="space-y-6">
              <TabsList>
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="posts">Posts ({posts.length})</TabsTrigger>
                <TabsTrigger value="timeline">Timeline</TabsTrigger>
                <TabsTrigger value="team">Team</TabsTrigger>
              </TabsList>

              {/* Overview Tab */}
              <TabsContent value="overview" className="space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
                  {/* Plan */}
                  <Card className="bg-panel border border-cmp-line flex flex-col h-full">
                    <CardHeader className="flex-shrink-0">
                      <div className="flex items-center justify-between">
                        <CardTitle>Plan</CardTitle>
                        {!isEditingPlan ? (
                          <div className="flex gap-2">
                            <Button
                              onClick={handleGenerateAIPlan}
                              variant="outline"
                              size="sm"
                              className="gap-2 h-6 flex-shrink-0"
                              disabled={projectLoading || isGeneratingPlan || isGeneratingSolution}
                            >
                              {isGeneratingPlan || isGeneratingSolution ? (
                                <>
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                  Generating...
                                </>
                              ) : (
                                <>
                                  <Sparkles className="w-4 h-4" />
                                  AI Posting Suggestions
                                </>
                              )}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={handleStartEditingPlan}
                              className="gap-2 h-6 flex-shrink-0"
                            >
                              <Edit className="w-4 h-4" />
                              Edit
                            </Button>
                          </div>
                        ) : (
                          <div className="flex gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={handleCancelEditingPlan}
                              className="gap-2 h-6 flex-shrink-0"
                              disabled={isUpdatingProject}
                            >
                              <X className="w-4 h-4" />
                              Cancel
                            </Button>
                            <Button
                              variant="default"
                              size="sm"
                              onClick={handleSavePlan}
                              className="gap-2 h-6 flex-shrink-0"
                              disabled={isUpdatingProject}
                            >
                              {isUpdatingProject ? (
                                <>
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                  Saving...
                                </>
                              ) : (
                                <>
                                  <Save className="w-4 h-4" />
                                  Save
                                </>
                              )}
                            </Button>
                          </div>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent className="flex-1 flex flex-col min-h-0">
                      <Textarea
                        value={isEditingPlan ? editedPlan : projectDisplay.plan}
                        onChange={(e) => setEditedPlan(e.target.value)}
                        readOnly={!isEditingPlan}
                        className="h-full resize-none bg-form-dark text-cmp-ink"
                        placeholder="Enter plan..."
                      />
                    </CardContent>
                  </Card>

                  {/* Right Column */}
                  <div className="space-y-6">
                    {/* Landing Page */}
                    <Card className="bg-panel border border-cmp-line">
                      <CardHeader>
                        <CardTitle>Landing Page</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <LinkIcon className="w-5 h-5 text-gray-400" />
                            <div>
                              <p className="text-cmp-ink">
                                {projectDisplay.landingPageUrl || 'No landing page'}
                              </p>
                              {projectDisplay.landingPageStatus && (
                                <p className="text-cmp-ink-2">
                                  Status: {projectDisplay.landingPageStatus}
                                </p>
                              )}
                            </div>
                          </div>
                          {projectDisplay.landingPageUrl ? (
                            <Button variant="outline" asChild>
                              <a
                                href={projectDisplay.landingPageUrl}
                                target="_blank"
                                rel="noreferrer"
                              >
                                View page ↗
                              </a>
                            </Button>
                          ) : (
                            <Button
                              variant="outline"
                              disabled
                              title="This campaign has no landing page"
                            >
                              View page
                            </Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>

                    {/* Progress Card */}
                    <Card className="bg-panel border border-cmp-line">
                      <CardHeader>
                        <CardTitle>Progress</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-cmp-ink-2">Overall</span>
                            <span className="text-cmp-ink">{projectDisplay.progress}%</span>
                          </div>
                          <Progress value={projectDisplay.progress} />
                        </div>

                        <div className="pt-4 border-t border-gray-200 space-y-2">
                          <div className="flex justify-between text-cmp-ink-2">
                            <span>Published</span>
                            <span className="text-cmp-ink">{statusCounts.published}</span>
                          </div>
                          <div className="flex justify-between text-cmp-ink-2">
                            <span>Scheduled</span>
                            <span className="text-cmp-ink">{statusCounts.scheduled}</span>
                          </div>
                          <div className="flex justify-between text-cmp-ink-2">
                            <span>In Review</span>
                            <span className="text-cmp-ink">{statusCounts['in-review']}</span>
                          </div>
                          <div className="flex justify-between text-cmp-ink-2">
                            <span>Draft</span>
                            <span className="text-cmp-ink">{statusCounts.draft}</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Project Notes */}
                    <Card className="bg-panel border border-cmp-line">
                      <CardHeader>
                        <CardTitle>Notes</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <Textarea
                          value={projectDisplay.notes || ''}
                          readOnly
                          placeholder="No notes added yet..."
                          className="min-h-32 resize-none bg-form-dark text-cmp-ink"
                        />
                      </CardContent>
                    </Card>
                  </div>
                </div>

                {/* Planned Posts Preview */}
                <Card className="bg-panel border border-cmp-line">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle>Planned Posts</CardTitle>
                      <Button variant="ghost" size="sm">
                        View All
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {posts.slice(0, 3).map((post) => {
                        const rawPost = rawPosts.find((p: any) => p.id === post.id);
                        const scheduledDate = rawPost?.scheduledDate || post.scheduledDate;
                        return (
                          <div
                            key={post.id}
                            onClick={() => handleEditPost(post.id)}
                            className="flex items-center justify-between p-3 bg-cmp-surface rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                          >
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              {/* Topic Thumbnail */}
                              {rawPost?.topic?.mediaUrl && (
                                <div className="w-12 h-12 rounded-lg overflow-hidden bg-cmp-surface flex-shrink-0">
                                  {rawPost.topic.mediaType === 'VIDEO' ? (
                                    <video
                                      src={rawPost.topic.mediaUrl}
                                      className="w-full h-full object-cover"
                                      controls={false}
                                      muted
                                    />
                                  ) : (
                                    <img
                                      src={rawPost.topic.mediaUrl}
                                      alt={rawPost.topic.title || 'Topic thumbnail'}
                                      className="w-full h-full object-cover"
                                    />
                                  )}
                                </div>
                              )}
                              {getStatusIcon(post.status)}
                              <div className="flex-1 min-w-0">
                                <p className="text-gray-900 font-medium">{post.title}</p>
                                <div className="flex items-center gap-2 mt-1 flex-wrap">
                                  {post.postType && (
                                    <Badge
                                      className="bg-cmp-surface text-cmp-ink-2 border border-cmp-line"
                                      variant="secondary"
                                    >
                                      {post.postType}
                                    </Badge>
                                  )}
                                  {rawPost?.targets && rawPost.targets.length > 0 ? (
                                    <>
                                      {rawPost.targets.slice(0, 3).map((target: any) => (
                                        <Badge
                                          key={target.id}
                                          className={`${getPlatformColor(target.platform)} flex items-center gap-1.5`}
                                          variant="secondary"
                                        >
                                          <PlatformIcon platform={target.platform} size={14} />
                                          {target.socialProfile?.profileName || target.platform}
                                        </Badge>
                                      ))}
                                      {rawPost.targets.length > 3 && (
                                        <Badge
                                          className="bg-cmp-surface text-cmp-ink-2"
                                          variant="secondary"
                                        >
                                          +{rawPost.targets.length - 3}
                                        </Badge>
                                      )}
                                    </>
                                  ) : (
                                    post.platforms.slice(0, 3).map((platform) => (
                                      <Badge
                                        key={platform}
                                        className={`${getPlatformColor(platform)} flex items-center gap-1.5`}
                                        variant="secondary"
                                      >
                                        <PlatformIcon platform={platform} size={14} />
                                        {platform}
                                      </Badge>
                                    ))
                                  )}
                                </div>
                                <div className="flex items-center gap-3 mt-1 text-xs text-gray-500 flex-wrap">
                                  {scheduledDate ? (
                                    <span className="font-medium text-cmp-bronze-2">
                                      Scheduled:{' '}
                                      {new Date(scheduledDate).toLocaleDateString('en-US', {
                                        month: 'short',
                                        day: 'numeric',
                                        year: 'numeric',
                                      })}
                                    </span>
                                  ) : null}
                                  <span>{post.assignedTo || 'Unassigned'}</span>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 ml-2">
                              <Badge className={getStatusColor(post.status)} variant="secondary">
                                {post.status}
                              </Badge>
                              <Eye className="w-4 h-4 text-gray-400" />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Posts Tab */}
              <TabsContent
                value="posts"
                className={viewMode === 'kanban' ? 'h-full flex flex-col' : 'space-y-6'}
              >
                {/* Posts Header */}
                <div
                  className={`flex items-center justify-between ${viewMode === 'kanban' ? 'flex-shrink-0 mb-6' : ''}`}
                >
                  <div className="flex gap-4 items-center">
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                      <SelectTrigger className="w-40">
                        <SelectValue placeholder="Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All ({statusCounts.all})</SelectItem>
                        <SelectItem value="draft">Draft ({statusCounts.draft})</SelectItem>
                        <SelectItem value="in-review">
                          In Review ({statusCounts['in-review']})
                        </SelectItem>
                        <SelectItem value="approved">Approved ({statusCounts.approved})</SelectItem>
                        <SelectItem value="scheduled">
                          Scheduled ({statusCounts.scheduled})
                        </SelectItem>
                        <SelectItem value="published">
                          Published ({statusCounts.published})
                        </SelectItem>
                      </SelectContent>
                    </Select>

                    <div className="flex gap-1 border border-gray-600 rounded-lg p-1 bg-gray-800">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setViewMode('grid')}
                        className={
                          viewMode === 'grid'
                            ? 'bg-cmp-bronze hover:bg-cmp-bronze-2 text-onfill'
                            : 'hover:bg-cmp-bronze-soft text-gray-300'
                        }
                      >
                        <LayoutGrid className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setViewMode('list')}
                        className={
                          viewMode === 'list'
                            ? 'bg-cmp-bronze hover:bg-cmp-bronze-2 text-onfill'
                            : 'hover:bg-cmp-bronze-soft text-gray-300'
                        }
                      >
                        <List className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setViewMode('kanban')}
                        className={
                          viewMode === 'kanban'
                            ? 'bg-cmp-bronze hover:bg-cmp-bronze-2 text-onfill'
                            : 'hover:bg-cmp-bronze-soft text-gray-300'
                        }
                      >
                        <Columns className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      onClick={handleGenerateAIPlan}
                      variant="outline"
                      className="gap-2"
                      disabled={projectLoading || isGeneratingPlan || isGeneratingSolution}
                    >
                      {isGeneratingPlan || isGeneratingSolution ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4" />
                          AI Posting Suggestions
                        </>
                      )}
                    </Button>
                    <Button onClick={onCreatePost} className="gap-2">
                      <Plus className="w-4 h-4" />
                      New Post
                    </Button>
                  </div>
                </div>

                {/* Posts Grid/List/Kanban */}
                {viewMode === 'kanban' ? (
                  <div className="flex-1 min-h-0 overflow-hidden">
                    <Card className="h-full flex flex-col bg-panel border border-cmp-line overflow-hidden">
                      <CardContent className="flex-1 overflow-y-auto overflow-x-auto min-h-0 p-0">
                        <KanbanBoard
                          posts={filteredPosts}
                          onPostStatusChange={handlePostStatusChange}
                        />
                      </CardContent>
                    </Card>
                  </div>
                ) : viewMode === 'grid' ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredPosts.map((post) => (
                      <Card
                        key={post.id}
                        className="hover:shadow-lg transition-shadow cursor-pointer bg-panel border border-cmp-line"
                      >
                        <CardHeader>
                          <div className="flex items-start justify-between mb-2">
                            <Badge className={getStatusColor(post.status)} variant="secondary">
                              {post.status}
                            </Badge>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm">
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent className="bg-core-dark">
                                <DropdownMenuItem onClick={() => handleEditPost(post.id)}>
                                  Edit
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleDuplicatePost(post.id)}>
                                  Duplicate
                                </DropdownMenuItem>
                                <DropdownMenuSub>
                                  <DropdownMenuSubTrigger>Change Status</DropdownMenuSubTrigger>
                                  <DropdownMenuSubContent className="bg-core-dark">
                                    <DropdownMenuItem
                                      onClick={() => handleChangeStatus(post.id, 'draft')}
                                      className="hover:bg-form-dark"
                                    >
                                      Draft
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={() => handleChangeStatus(post.id, 'in-review')}
                                      className="hover:bg-form-dark"
                                    >
                                      In Review
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={() => handleChangeStatus(post.id, 'approved')}
                                      className="hover:bg-form-dark"
                                    >
                                      Approved
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={() => handleChangeStatus(post.id, 'scheduled')}
                                      className="hover:bg-form-dark"
                                    >
                                      Scheduled
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={() => handleChangeStatus(post.id, 'published')}
                                      className="hover:bg-form-dark"
                                    >
                                      Published
                                    </DropdownMenuItem>
                                  </DropdownMenuSubContent>
                                </DropdownMenuSub>
                                <DropdownMenuItem
                                  className="text-cmp-crit"
                                  onClick={() => handleDeletePost(post.id)}
                                >
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                          <CardTitle className="text-white">{post.title}</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          {/* Topic Thumbnail */}
                          {post.topic?.mediaUrl && (
                            <div className="w-full aspect-video rounded-lg overflow-hidden bg-gray-800">
                              {post.topic.mediaType === 'VIDEO' ? (
                                <video
                                  src={post.topic.mediaUrl}
                                  className="w-full h-full object-cover"
                                  controls={false}
                                  muted
                                />
                              ) : (
                                <img
                                  src={post.topic.mediaUrl}
                                  alt={post.topic.title || 'Topic thumbnail'}
                                  className="w-full h-full object-cover"
                                />
                              )}
                            </div>
                          )}
                          <p className="text-gray-300 line-clamp-2">{post.content}</p>

                          {/* Post Type */}
                          {post.postType && (
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-gray-400">Type:</span>
                              <Badge
                                className="bg-cmp-surface text-cmp-ink-2 border border-cmp-line"
                                variant="secondary"
                              >
                                {post.postType}
                              </Badge>
                            </div>
                          )}

                          {/* Social Accounts */}
                          <div className="flex flex-wrap gap-2">
                            {post.targets && post.targets.length > 0
                              ? post.targets.map((target: any) => (
                                  <Badge
                                    key={target.id}
                                    className={`${getPlatformColor(target.platform)} flex items-center gap-1.5`}
                                    variant="secondary"
                                  >
                                    <PlatformIcon platform={target.platform} size={14} />
                                    {target.socialProfile?.profileName || target.platform}
                                  </Badge>
                                ))
                              : post.platforms.map((platform) => (
                                  <Badge
                                    key={platform}
                                    className={`${getPlatformColor(platform)} flex items-center gap-1.5`}
                                    variant="secondary"
                                  >
                                    <PlatformIcon platform={platform} size={14} />
                                    {platform}
                                  </Badge>
                                ))}
                          </div>

                          {/* Notes */}
                          {post.notes && (
                            <div className="pt-2">
                              <p className="text-xs text-gray-400 mb-1">Notes:</p>
                              <p className="text-sm text-gray-300 line-clamp-2">{post.notes}</p>
                            </div>
                          )}

                          <div className="pt-4 border-t border-gray-600 space-y-2">
                            {post.assignedTo && (
                              <div className="flex items-center justify-between text-gray-300">
                                <span>Assigned to</span>
                                <span className="text-white">{post.assignedTo}</span>
                              </div>
                            )}
                            {post.scheduledDate && (
                              <div className="flex items-center justify-between text-gray-300">
                                <span>Scheduled date</span>
                                <span className="text-white font-medium">
                                  {new Date(post.scheduledDate).toLocaleDateString()}
                                </span>
                              </div>
                            )}
                            <div className="flex items-center justify-between text-gray-300">
                              <span>Due date</span>
                              <span className="text-white">
                                {new Date(post.dueDate).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <Card className="bg-panel border border-cmp-line">
                    <CardContent className="p-0">
                      <div className="divide-y divide-gray-600">
                        {filteredPosts.map((post) => (
                          <div key={post.id} className="p-4 cursor-pointer transition-colors">
                            <div className="flex items-center justify-between gap-6">
                              {/* Left: Thumbnail, Status Icon and Title */}
                              <div className="flex items-center gap-4 flex-shrink-0">
                                {/* Topic Thumbnail */}
                                {post.topic?.mediaUrl && (
                                  <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-800 flex-shrink-0">
                                    {post.topic.mediaType === 'VIDEO' ? (
                                      <video
                                        src={post.topic.mediaUrl}
                                        className="w-full h-full object-cover"
                                        controls={false}
                                        muted
                                      />
                                    ) : (
                                      <img
                                        src={post.topic.mediaUrl}
                                        alt={post.topic.title || 'Topic thumbnail'}
                                        className="w-full h-full object-cover"
                                      />
                                    )}
                                  </div>
                                )}
                                {getStatusIcon(post.status)}
                                <div className="min-w-0">
                                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                                    <p className="text-white font-medium">{post.title}</p>
                                    <Badge
                                      className={getStatusColor(post.status)}
                                      variant="secondary"
                                    >
                                      {post.status}
                                    </Badge>
                                    {post.postType && (
                                      <Badge
                                        className="bg-cmp-surface text-cmp-ink-2 border border-cmp-line"
                                        variant="secondary"
                                      >
                                        {post.postType}
                                      </Badge>
                                    )}
                                  </div>
                                  <p
                                    className="text-gray-300 text-sm line-clamp-4"
                                    style={{
                                      width: '40%',
                                      maxWidth: '40%',
                                      wordBreak: 'break-word',
                                      overflow: 'hidden',
                                      overflowWrap: 'break-word',
                                    }}
                                  >
                                    {post.content}
                                  </p>
                                </div>
                              </div>

                              {/* Middle: Social Accounts */}
                              <div className="flex items-center gap-2 flex-1 min-w-0 justify-center">
                                {post.targets && post.targets.length > 0 ? (
                                  <>
                                    {post.targets.slice(0, 4).map((target: any) => (
                                      <Badge
                                        key={target.id}
                                        className={`${getPlatformColor(target.platform)} flex items-center gap-1.5`}
                                        variant="secondary"
                                      >
                                        <PlatformIcon platform={target.platform} size={14} />
                                        {target.socialProfile?.profileName || target.platform}
                                      </Badge>
                                    ))}
                                    {post.targets.length > 4 && (
                                      <Badge
                                        className="bg-cmp-surface text-cmp-ink-2"
                                        variant="secondary"
                                      >
                                        +{post.targets.length - 4}
                                      </Badge>
                                    )}
                                  </>
                                ) : (
                                  post.platforms.slice(0, 4).map((platform) => (
                                    <Badge
                                      key={platform}
                                      className={`${getPlatformColor(platform)} flex items-center gap-1.5`}
                                      variant="secondary"
                                    >
                                      <PlatformIcon platform={platform} size={14} />
                                      {platform}
                                    </Badge>
                                  ))
                                )}
                              </div>

                              {/* Right: Dates and Assignment */}
                              <div className="flex items-center gap-6 text-gray-300 flex-shrink-0">
                                {post.scheduledDate && (
                                  <div className="text-right">
                                    <p className="text-gray-400 text-xs">Scheduled</p>
                                    <p className="text-white font-medium text-sm">
                                      {new Date(post.scheduledDate).toLocaleDateString('en-US', {
                                        month: 'short',
                                        day: 'numeric',
                                      })}
                                    </p>
                                  </div>
                                )}
                                <div className="text-right">
                                  <p className="text-gray-400 text-xs">Due</p>
                                  <p className="text-white text-sm">
                                    {new Date(post.dueDate).toLocaleDateString('en-US', {
                                      month: 'short',
                                      day: 'numeric',
                                    })}
                                  </p>
                                </div>
                                <div className="text-right min-w-[100px]">
                                  <p className="text-gray-400 text-xs">Assigned</p>
                                  <p className="text-white text-sm truncate">
                                    {post.assignedTo || 'Unassigned'}
                                  </p>
                                </div>
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="text-gray-400 hover:text-white"
                                    >
                                      <MoreVertical className="w-4 h-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent className="bg-core-dark">
                                    <DropdownMenuItem
                                      onClick={() => handleEditPost(post.id)}
                                      className="hover:bg-form-dark"
                                    >
                                      Edit
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={() => handleDuplicatePost(post.id)}
                                      className="hover:bg-form-dark"
                                    >
                                      Duplicate
                                    </DropdownMenuItem>
                                    <DropdownMenuSub>
                                      <DropdownMenuSubTrigger>Change Status</DropdownMenuSubTrigger>
                                      <DropdownMenuSubContent className="bg-core-dark">
                                        <DropdownMenuItem
                                          onClick={() => handleChangeStatus(post.id, 'draft')}
                                          className="hover:bg-form-dark"
                                        >
                                          Draft
                                        </DropdownMenuItem>
                                        <DropdownMenuItem
                                          onClick={() => handleChangeStatus(post.id, 'in-review')}
                                          className="hover:bg-form-dark"
                                        >
                                          In Review
                                        </DropdownMenuItem>
                                        <DropdownMenuItem
                                          onClick={() => handleChangeStatus(post.id, 'approved')}
                                          className="hover:bg-form-dark"
                                        >
                                          Approved
                                        </DropdownMenuItem>
                                        <DropdownMenuItem
                                          onClick={() => handleChangeStatus(post.id, 'scheduled')}
                                          className="hover:bg-form-dark"
                                        >
                                          Scheduled
                                        </DropdownMenuItem>
                                        <DropdownMenuItem
                                          onClick={() => handleChangeStatus(post.id, 'published')}
                                          className="hover:bg-form-dark"
                                        >
                                          Published
                                        </DropdownMenuItem>
                                      </DropdownMenuSubContent>
                                    </DropdownMenuSub>
                                    <DropdownMenuItem
                                      className="text-cmp-crit"
                                      onClick={() => handleDeletePost(post.id)}
                                    >
                                      Delete
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {filteredPosts.length === 0 && viewMode !== 'kanban' && (
                  <div className="text-center py-12">
                    <p className="text-gray-300">No posts found matching the selected status</p>
                  </div>
                )}
              </TabsContent>

              {/* Timeline Tab */}
              <TabsContent value="timeline">
                <Card className="bg-panel border border-cmp-line">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle>Timeline</CardTitle>
                        <CardDescription>
                          View all posts scheduled across the campaign timeline
                        </CardDescription>
                      </div>
                      <div className="flex gap-1 border border-gray-200 rounded-lg p-1">
                        <Button
                          variant={timelineView === 'list' ? 'secondary' : 'ghost'}
                          size="sm"
                          onClick={() => setTimelineView('list')}
                        >
                          <List className="w-4 h-4" />
                        </Button>
                        <Button
                          variant={timelineView === 'calendar' ? 'secondary' : 'ghost'}
                          size="sm"
                          onClick={() => setTimelineView('calendar')}
                        >
                          <CalendarDays className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {timelineView === 'list' ? (
                      <div className="space-y-4">
                        {posts
                          .filter((p) => p.scheduledDate || p.dueDate)
                          .sort(
                            (a, b) =>
                              new Date(a.scheduledDate || a.dueDate).getTime() -
                              new Date(b.scheduledDate || b.dueDate).getTime()
                          )
                          .map((post, idx) => {
                            const rawPost = rawPosts.find((p: any) => p.id === post.id);
                            return (
                              <div key={post.id} className="flex gap-4">
                                <div className="flex flex-col items-center">
                                  <div
                                    className={`w-3 h-3 rounded-full ${
                                      post.status === 'published'
                                        ? 'bg-cmp-ok'
                                        : post.status === 'scheduled'
                                          ? 'bg-cmp-bronze'
                                          : 'bg-gray-400'
                                    }`}
                                  ></div>
                                  {idx <
                                    posts.filter((p) => p.scheduledDate || p.dueDate).length -
                                      1 && <div className="w-0.5 h-full bg-gray-600 my-1"></div>}
                                </div>
                                <div className="flex-1 pb-6">
                                  <div className="flex items-start justify-between mb-2 gap-4">
                                    <div className="flex-1">
                                      <div className="flex items-start gap-3">
                                        {/* Topic Thumbnail */}
                                        {rawPost?.topic?.mediaUrl && (
                                          <div className="w-20 h-20 rounded-lg overflow-hidden bg-gray-800 flex-shrink-0">
                                            {rawPost.topic.mediaType === 'VIDEO' ? (
                                              <video
                                                src={rawPost.topic.mediaUrl}
                                                className="w-full h-full object-cover"
                                                controls={false}
                                                muted
                                              />
                                            ) : (
                                              <img
                                                src={rawPost.topic.mediaUrl}
                                                alt={rawPost.topic.title || 'Topic thumbnail'}
                                                className="w-full h-full object-cover"
                                              />
                                            )}
                                          </div>
                                        )}
                                        <div className="flex-1">
                                          <p className="text-white">{post.title}</p>
                                          <p className="text-gray-300">
                                            {new Date(
                                              post.scheduledDate || post.dueDate
                                            ).toLocaleDateString()}
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                    <Badge
                                      className={getStatusColor(post.status)}
                                      variant="secondary"
                                    >
                                      {post.status}
                                    </Badge>
                                  </div>
                                  <div className="flex gap-2 flex-wrap">
                                    {rawPost?.targets && rawPost.targets.length > 0
                                      ? rawPost.targets.map((target: any) => (
                                          <Badge
                                            key={target.id}
                                            className={`${getPlatformColor(target.platform)} flex items-center gap-1.5`}
                                            variant="secondary"
                                          >
                                            <PlatformIcon platform={target.platform} size={14} />
                                            {target.socialProfile?.profileName || target.platform}
                                          </Badge>
                                        ))
                                      : post.platforms.map((platform) => (
                                          <Badge
                                            key={platform}
                                            className={`${getPlatformColor(platform)} flex items-center gap-1.5`}
                                            variant="secondary"
                                          >
                                            <PlatformIcon platform={platform} size={14} />
                                            {platform}
                                          </Badge>
                                        ))}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    ) : (
                      <TimelineCalendar posts={posts} projectName={projectData?.project?.name} />
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Team Tab */}
              <TabsContent value="team">
                <Card className="bg-panel border border-cmp-line">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle>Team Members</CardTitle>
                      <Button
                        variant="outline"
                        className="gap-2"
                        onClick={() => setIsAddMemberModalOpen(true)}
                      >
                        <Plus className="w-4 h-4" />
                        Add Member
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {projectDisplay.teamMembers.map(
                        (member: { name: string; role: string; avatar: string }) => {
                          const memberPosts = posts.filter((p) => p.assignedTo === member.name);
                          return (
                            <div
                              key={member.name}
                              className="flex items-center justify-between p-4 bg-cmp-surface rounded-lg"
                            >
                              <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-full bg-cmp-bronze-soft flex items-center justify-center text-cmp-bronze-2">
                                  {member.avatar}
                                </div>
                                <div>
                                  <p className="text-gray-900">{member.name}</p>
                                  <p className="text-gray-500">{member.role}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-6">
                                <div>
                                  <p className="text-gray-500">Assigned Posts</p>
                                  <p className="text-gray-900">{memberPosts.length}</p>
                                </div>
                                <div>
                                  <p className="text-gray-500">Completed</p>
                                  <p className="text-gray-900">
                                    {
                                      memberPosts.filter(
                                        (p) => p.status === 'published' || p.status === 'scheduled'
                                      ).length
                                    }
                                  </p>
                                </div>
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="sm">
                                      <MoreVertical className="w-4 h-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent>
                                    <DropdownMenuItem>View Posts</DropdownMenuItem>
                                    <DropdownMenuItem>Change Role</DropdownMenuItem>
                                    <DropdownMenuItem className="text-cmp-crit">
                                      Remove
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            </div>
                          );
                        }
                      )}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>

      {/* Add Team Member Modal */}
      {project && (
        <AddTeamMemberModal
          open={isAddMemberModalOpen}
          onClose={() => setIsAddMemberModalOpen(false)}
          projectId={projectId}
          accountId={project.accountId}
          existingTeamMemberIds={
            project.teamMembers?.map((tm: any) => tm.user?.id).filter(Boolean) || []
          }
        />
      )}

      {/* Edit Project Modal */}
      {project && (
        <CreateProjectModal
          open={isEditProjectModalOpen}
          onClose={() => setIsEditProjectModalOpen(false)}
          project={{
            id: project.id,
            name: project.name,
            plan: project.plan || '',
            deadline: project.deadline,
            campaignStory: project.campaignStory || '',
            landingPageUrl: project.landingPageUrl || '',
          }}
        />
      )}

      {/* Edit Post Modal */}
      <CreatePostModal
        open={isEditPostModalOpen}
        onClose={() => {
          setIsEditPostModalOpen(false);
          setPostToEdit(null);
        }}
        projectId={projectId}
        postToEdit={postToEdit}
        teamMembers={project.teamMembers || []}
      />

      {/* Create Post from AI Plan Modal */}
      {postFromPlanData && (
        <CreatePostModal
          open={isCreatePostFromPlanOpen}
          onClose={() => {
            setIsCreatePostFromPlanOpen(false);
            setPostFromPlanData(null);
          }}
          projectId={projectId}
          teamMembers={project.teamMembers || []}
          initialValues={{
            platforms: postFromPlanData.platforms,
            content: postFromPlanData.content,
            title: postFromPlanData.title,
            selectedProfiles: postFromPlanData.selectedProfiles,
          }}
        />
      )}

      {/* AI Social Media Plan Modal */}
      <Dialog open={isAIPlanModalOpen} onOpenChange={setIsAIPlanModalOpen}>
        <DialogContent className="max-w-[95vw] sm:max-w-5xl max-h-[80vh] overflow-y-auto bg-core-dark text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5" />
              AI Social Media Plan
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              AI-generated social media strategy based on your project brief
            </DialogDescription>
          </DialogHeader>

          {isGeneratingPlan ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-cmp-bronze mb-4" />
              <p className="text-gray-400">Generating your social media plan...</p>
            </div>
          ) : aiPlan ? (
            <div className="space-y-6">
              {/* Solution Overview */}
              <div className="space-y-2">
                <h3 className="text-lg font-semibold text-white">Strategy Overview</h3>
                <div className="bg-form-dark rounded-lg p-4">
                  <p className="text-gray-300 whitespace-pre-wrap">{aiPlan.solution}</p>
                </div>
              </div>

              {/* Action Plan */}
              {aiPlan.plan && aiPlan.plan.length > 0 && (
                <div className="space-y-2">
                  <h3 className="text-lg font-semibold text-white">Action Plan</h3>
                  <div className="space-y-2">
                    {aiPlan.plan.map((item, index) => {
                      const parsed = parsePlanItem(item);
                      const hasPlatforms = parsed.platforms.length > 0;
                      // Skip items that are warnings/guidelines (usually start with "-")
                      const isGuideline = item.trim().startsWith('-');

                      if (isGuideline) {
                        return null;
                      }

                      return (
                        <div
                          key={index}
                          className="bg-form-dark rounded-lg p-4 flex items-start gap-3"
                        >
                          <div className="flex-shrink-0 w-6 h-6 rounded-full bg-cmp-bronze-soft text-cmp-bronze-2 flex items-center justify-center text-sm font-semibold">
                            {index + 1}
                          </div>
                          <div className="flex-1 space-y-2">
                            <p className="text-gray-300">{item}</p>
                            {hasPlatforms && (
                              <div className="flex items-center justify-between gap-2">
                                <div className="flex flex-wrap gap-2">
                                  {parsed.platforms.map((platform) => (
                                    <Badge
                                      key={platform}
                                      variant="secondary"
                                      className="bg-cmp-bronze-soft text-cmp-bronze-2 border-cmp-line"
                                    >
                                      {platform}
                                    </Badge>
                                  ))}
                                </div>
                                <Button
                                  size="sm"
                                  onClick={() => handleCreatePostFromPlanItem(item)}
                                  className="gap-2 h-9 flex-shrink-0"
                                >
                                  <Plus className="w-4 h-4" />
                                  Create Post
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="py-12 text-center text-gray-400">
              <p>No plan generated yet. Click "AI Posting Suggestions" to get started.</p>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsAIPlanModalOpen(false);
                setAiPlan(null);
              }}
            >
              Close
            </Button>
            {aiPlan && (
              <>
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsAIPlanModalOpen(false);
                    setAiPlan(null);
                    handleGenerateAIPlan();
                  }}
                  className="gap-2"
                >
                  <Sparkles className="w-4 h-4" />
                  Generate a New Plan
                </Button>
                <Button
                  onClick={handleSaveAIPlanToProject}
                  className="gap-2"
                  disabled={isUpdatingProject}
                >
                  {isUpdatingProject ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      Save to Plan
                    </>
                  )}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
